import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePromoCodeList } from './hooks'
import { ColumnsType } from 'antd/lib/table'
import moment from 'moment/moment'
import dayjs from 'dayjs'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path, role } from '../../../../libs'
import { PromoCode } from './types'
import {
  Button,
  DatePicker,
  Flex,
  Input,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd'
import CIcon from '@coreui/icons-react'
import { cilDelete, cilPencil } from '@coreui/icons'
import { Link } from 'react-router-dom'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import { submitEditPromoCode } from '../edit/model'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { useUrlFilters } from '../../../../libs/useUrlFilters'

const { Text } = Typography
const { RangePicker } = DatePicker

// ── Filter config ──────────────────────────────────────────
const FILTER_CONFIG = {
  code: { type: 'string' as const },
  expiresFrom: { type: 'string' as const },
  expiresTo: { type: 'string' as const },
  disabledAt: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'id', pagination: true },
  sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
}

export const PromoCodesList: React.FC = () => {
  const userRole = useStore($userRole)
  const {
    promoCodeList,
    onChangePromoCodeList,
    totalCountOfPromoCodeList,
    loading,
  } = usePromoCodeList()

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'id'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'

  // Local input state — applied to the URL only on Enter/blur
  const [inputCode, setInputCode] = useState(filters.code || '')
  useEffect(() => {
    const value = inputCode.trim()

    const timer = setTimeout(() => {
      if (value.length >= 3) {
        if (filters.code !== value) {
          setFilters({
            code: value,
            page: undefined,
          } as any)
        }
      } else if (filters.code) {
        setFilters({
          code: undefined,
          page: undefined,
        } as any)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [inputCode, filters.code])

  // ── Expiration range from URL ───────────────────────────
  const expiresRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null =
    filters.expiresFrom || filters.expiresTo
      ? [
          filters.expiresFrom ? dayjs(filters.expiresFrom) : null,
          filters.expiresTo ? dayjs(filters.expiresTo) : null,
        ]
      : null

  // ── Build API searchText from URL state ─────────────────
  const buildSearchText = useCallback(() => {
    const searchText: Record<string, any> = {}
    if (filters.code) searchText.code = filters.code
    if (filters.expiresFrom || filters.expiresTo) {
      searchText.expiresAt = [
        filters.expiresFrom ? dayjs(filters.expiresFrom) : null,
        filters.expiresTo ? dayjs(filters.expiresTo) : null,
      ]
    }
    return searchText
  }, [filters])

  // ── antd column filter (disabledAt) consumed by the model ─
  const filtersState = useMemo(
    () => (filters.disabledAt ? { disabledAt: [filters.disabledAt] } : {}),
    [filters.disabledAt],
  )

  // ── Fetch data when filters change ──────────────────────
  useEffect(() => {
    onChangePromoCodeList({
      searchText: buildSearchText(),
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      filters: filtersState,
    })
  }, [filters, page, pageSize, orderBy, orderDirection])

  // ── Promo-code text search (applied on Enter/blur) ──────
  const applyCode = (value: string) => {
    setFilters({ code: value.trim() || undefined, page: undefined } as any)
  }

  const clearCode = () => {
    setInputCode('')
    setFilters({ code: undefined, page: undefined } as any)
  }

  // ── Active filter tags ──────────────────────────────────
  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.code)
      tags.push({
        key: 'code',
        label: `Promo-code: ${filters.code}`,
        clear: clearCode,
      })
    if (filters.expiresFrom || filters.expiresTo)
      tags.push({
        key: 'expires',
        label: `Expiration: ${filters.expiresFrom || '…'} — ${
          filters.expiresTo || '…'
        }`,
        clear: () =>
          setFilters({
            expiresFrom: undefined,
            expiresTo: undefined,
            page: undefined,
          } as any),
      })
    if (filters.disabledAt)
      tags.push({
        key: 'disabledAt',
        label: `Status: ${
          filters.disabledAt === 'null' ? 'Active' : 'Disabled'
        }`,
        clear: () =>
          setFilters({ disabledAt: undefined, page: undefined } as any),
      })
    return tags
  }, [filters])

  // ── Controlled sort order for a sortable column ─────────
  const sortOrderFor = (
    field: string,
  ): 'ascend' | 'descend' | null =>
    orderBy === field ? (orderDirection === '-' ? 'descend' : 'ascend') : null

  const columns: ColumnsType<PromoCode> = [
    {
      title: 'Promo-code',
      dataIndex: 'code',
      // render: (code: string) => (
      //   <Highlighter
      //     searchWords={[inputCode.trim()]}
      //     highlightStyle={{
      //       backgroundColor: 'var(--color-yellow-50)',
      //       padding: 0,
      //     }}
      //     autoEscape
      //     textToHighlight={code ?? ''}
      //   />
      // ),
    },
    {
      title: 'Active',
      dataIndex: 'disabledAt',
      render: (value) => (
        <div style={{ textAlign: 'center' }}>
          {value === null ? (
            <CheckCircleOutlined className="text-success" />
          ) : (
            <CloseSquareOutlined className="text-danger" />
          )}
        </div>
      ),
    },
    {
      title: 'Expiration date',
      dataIndex: 'expiresAt',
      sortOrder: sortOrderFor('expiresAt'),
      sorter: (a, b) =>
        // @ts-ignore
        new Date(moment(a.updatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')) -
        // @ts-ignore
        new Date(moment(b.updatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')),
    },
    {
      title: 'Bonus %',
      dataIndex: 'bonusPercent',
      sorter: true,
      sortOrder: sortOrderFor('bonusPercent'),
    },
    {
      title: 'Activated',
      dataIndex: 'activatedCount',
      sorter: true,
      sortOrder: sortOrderFor('activatedCount'),
    },
    {
      title: 'Used',
      dataIndex: 'usageCount',
      sorter: true,
      sortOrder: sortOrderFor('usageCount'),
    },
    {
      title: 'Deposited',
      dataIndex: 'depositAmountUsd',
      sorter: true,
      sortOrder: sortOrderFor('depositAmountUsd'),
    },
    {
      title: 'Created at',
      dataIndex: 'addedAt',
      sortOrder: sortOrderFor('addedAt'),
      sorter: (a, b) =>
        // @ts-ignore
        new Date(moment(a.updatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')) -
        // @ts-ignore
        new Date(moment(b.updatedAt, 'MMMM Do YYYY, h:mm:ss a').format('LLL')),
    },
    {
      title: 'User id',
      dataIndex: 'userId',
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      render: (createdBy) => {
        return (
          <div>
            <Link
              to={path.users.details(createdBy?.id)}
              style={{ textDecoration: 'none' }}
            >
              {createdBy?.firstName} {createdBy?.lastName}
            </Link>
          </div>
        )
      },
    },
    {
      title: 'Usage',
      dataIndex: 'usage',
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item) => {
        return (
          <>
            <CButton
              hidden={
                Array.isArray(userRole) &&
                [
                  role.partnership.seniorPartnerManager(),
                  role.partnership.juniorPartnerManager(),
                ].some((restrictedRole) => userRole.includes(restrictedRole))
              }
              color="secondary"
              className="me-2"
              title="Edit"
              href={path.depositBonus.promoCodes.edit(item.id)}
            >
              <CIcon icon={cilPencil} />
            </CButton>
            <CButton
              hidden={
                Array.isArray(userRole) &&
                [
                  role.partnership.seniorPartnerManager(),
                  role.partnership.juniorPartnerManager(),
                ].some((restrictedRole) => userRole.includes(restrictedRole))
              }
              color="secondary"
              className="me-2"
              title="Delete"
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure you want to delete this promo code?',
                  )
                ) {
                  submitEditPromoCode({
                    id: item.id,
                    isDisabled: true,
                  })
                }
              }}
            >
              <CIcon icon={cilDelete} />
            </CButton>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <CButton
        hidden={
          Array.isArray(userRole) &&
          [
            role.partnership.seniorPartnerManager(),
            role.partnership.juniorPartnerManager(),
          ].some((restrictedRole) => userRole.includes(restrictedRole))
        }
        color="primary"
        className="mb-2"
        href={path.depositBonus.promoCodes.create()}
      >
        Create promo codes
      </CButton>

      {/* ── Filters panel ─────────────────────────────────── */}
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        {/* Row 1: promo-code search + status */}
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
          <Input
            prefix={
              <SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />
            }
            placeholder="Promo-code"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
          <Select
            placeholder="Active / Disabled"
            value={filters.disabledAt || undefined}
            onChange={(value) =>
              setFilters({
                disabledAt: value || undefined,
                page: undefined,
              } as any)
            }
            allowClear
            options={[
              { label: 'Active', value: 'null' },
              { label: 'Disabled', value: 'not null' },
            ]}
            style={{ width: 160 }}
          />
        </Flex>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: 'var(--crm-border, #e2e5ec)',
            margin: '0 0 10px',
          }}
        />

        {/* Row 2: expiration date range */}
        <Flex gap={10} wrap="wrap" align="center">
          <RangePicker
            value={expiresRange}
            allowEmpty={[true, true]}
            onChange={(vals) =>
              setFilters({
                expiresFrom: vals?.[0]
                  ? vals[0].format('YYYY-MM-DD')
                  : undefined,
                expiresTo: vals?.[1] ? vals[1].format('YYYY-MM-DD') : undefined,
                page: undefined,
              } as any)
            }
            format="YYYY-MM-DD"
            placeholder={['Expiration from', 'to']}
            style={{ width: 260 }}
          />
        </Flex>

        {/* Row 3: active filter tags */}
        {activeTags.length > 0 && (
          <>
            <div
              style={{
                height: 1,
                background: 'var(--crm-border, #e2e5ec)',
                margin: '10px 0 8px',
              }}
            />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((tag) => (
                <Tag
                  key={tag.key}
                  closable
                  onClose={tag.clear}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {tag.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputCode('')
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────── */}
      <Table
        dataSource={promoCodeList}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="promoCodesID"
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfPromoCodeList,
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current)
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          if (sorter.order) {
            // @ts-ignore
            updates.sortField = sorter.field === 'id' ? undefined : sorter.field
            // @ts-ignore
            updates.sortOrder = sorter.order === 'descend' ? undefined : 'ascend'
          } else {
            updates.sortField = undefined
            updates.sortOrder = undefined
          }
          setFilters(updates as any)
        }}
      />
    </CCardBody>
  )
}
